import { Component, OnInit, Input, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../../shared/material/material.module';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AccessPlan } from '../../../core/generated';
import { PlanPaymentDataService } from '../plan-payment-container/services/plan-payment-data.service';
import { PlanDetailsModalComponent } from '../user-plans/plan-details-modal/plan-details-modal.component';
import { fadeIn, listStagger } from '../shared/animations';

/**
 * TitularPendingPlansComponent
 * Exibe planos do titular aguardando aceitação do pagador externo
 * 
 * Requirements: 2.4
 * - Buscar planos com payerCnpj != null e payerAccepted == null
 * - Exibir lista de planos com status "Aguardando resposta do pagador"
 * - Permitir visualizar detalhes do plano
 */
@Component({
  selector: 'app-titular-pending-plans',
  standalone: true,
  imports: [CommonModule, MaterialModule],
  templateUrl: './titular-pending-plans.component.html',
  styleUrl: './titular-pending-plans.component.scss',
  animations: [fadeIn, listStagger]
})
export class TitularPendingPlansComponent implements OnInit {
  @Input() cpfcnpj: string = '';

  private dataService = inject(PlanPaymentDataService);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);

  plans = signal<AccessPlan[]>([]);
  isLoading = signal(false);

  ngOnInit(): void {
    this.loadPendingPlans();
  }

  /**
   * Carrega planos do titular aguardando aceitação do pagador externo
   * Filtra planos onde payerCnpj != null e payerAccepted == null
   * Requirement: 2.4
   */
  loadPendingPlans(): void {
    if (!this.cpfcnpj) return;
    
    this.isLoading.set(true);
    this.dataService.loadUserPlans(this.cpfcnpj, true).subscribe({
      next: (allPlans) => {
        // Filtrar apenas planos com pagador externo aguardando aceitação
        const pendingPlans = allPlans.filter(plan => 
          plan.payerCnpj && 
          plan.payerCnpj !== this.cpfcnpj && 
          plan.payerAccepted === null
        );
        this.plans.set(pendingPlans);
        this.isLoading.set(false);
      },
      error: () => {
        this.snackBar.open('Erro ao carregar planos pendentes.', 'Fechar', { duration: 4000 });
        this.isLoading.set(false);
      }
    });
  }

  /**
   * Abre modal de detalhes do plano
   * Requirement: 2.4
   */
  viewPlanDetails(plan: AccessPlan): void {
    this.dialog.open(PlanDetailsModalComponent, {
      data: { plan },
      width: '800px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      panelClass: 'plan-details-modal'
    });
  }

  /**
   * Formata valor monetário para exibição
   */
  formatCurrency(value?: number): string {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value ?? 0);
  }

  /**
   * Formata CNPJ para exibição
   */
  formatCnpj(cnpj?: string): string {
    if (!cnpj) return 'N/A';
    return cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  }
}
